import React from "react";
import { useTable, useSortBy, usePagination } from "react-table";

const ReusableTable = ({ columns, data, hidePagination = false }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      autoResetPage: false,
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="w-full">
      <div className="overflow-x-auto rounded-lg border border-gray-100 shadow-sm bg-white">
        <table
          {...getTableProps()}
          className="table-fixed min-w-full w-full text-[11px] text-left text-gray-700"
        >
          <thead className="bg-[#fcfcff] font-semibold border-b border-gray-100">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                    style={{ width: column.size }}
                    className="px-6 py-4 text-[11px] font-extrabold uppercase tracking-[0.1em] text-[#6366F1]"
                  >
                    <div className={`flex items-center gap-1 ${column.id === 'ui_menu' ? 'justify-start pl-2' : 'justify-center'}`}>
                      {column.render("Header")}
                      <span>
                        {column.isSorted ? (column.isSortedDesc ? " ↓" : " ↑") : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()} className="divide-y divide-gray-50">
            {page.map((row) => {
              prepareRow(row);

              // Check if this row is a section header
              if (row.original.isSection) {
                return (
                  <tr key={row.id} className="bg-[#FBFCFE]">
                    <td
                      colSpan={columns.length}
                      className="px-8 py-3.5 text-[10px] font-bold text-gray-500 uppercase tracking-widest border-y border-gray-100/50 text-left"
                    >
                      {row.original.sectionTitle}
                    </td>
                  </tr>
                );
              }

              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  className="hover:bg-[#F9FAFF] transition-colors"
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      style={{ width: cell.column.size }}
                      className="px-6 py-4 text-[12px] text-center whitespace-normal"
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table >
      </div >

      {/* Pagination Container */}
      {!hidePagination && (
        <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4 px-2 mb-4">
          {/* Left Side: Page Info */}
          <div className="text-sm text-gray-500 font-medium">
            Page {pageIndex + 1} of {pageOptions.length}
          </div>

          {/* Right Side: Navigation Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              Previous
            </button>

            <div className="flex items-center gap-1 mx-2">
              {pageOptions.map((option, index) => {
                // Only show a limited number of page buttons if there are many pages
                // For now, let's show all or handle basic cases
                const pageNum = index + 1;
                const isCurrent = pageIndex === index;

                // Only show buttons around current page if there are many pages
                if (
                  pageOptions.length > 7 &&
                  pageNum !== 1 &&
                  pageNum !== pageOptions.length &&
                  (pageNum < pageIndex - 1 || pageNum > pageIndex + 3)
                ) {
                  if (pageNum === pageIndex - 2 || pageNum === pageIndex + 4) {
                    return <span key={index} className="px-2 text-gray-400">...</span>;
                  }
                  return null;
                }

                return (
                  <button
                    key={index}
                    onClick={() => gotoPage(index)}
                    className={`w-9 h-9 flex items-center justify-center text-sm font-semibold rounded-md transition-all ${isCurrent
                      ? "bg-[#6366F1] text-white shadow-md shadow-indigo-100"
                      : "text-gray-600 hover:bg-gray-100 bg-white border border-transparent"
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div >
  );
};

export default ReusableTable;
